/**
 * "Which bookings end soon", "what bookings expire this month", "any bookings
 * ending next week", "when does my booking end" — the active bookings whose
 * last day is still to come, soonest first.
 *
 * A supplier asks to know which customers to talk to about staying on and how
 * many spaces are about to free up; a buyer asks so equipment is not left on a
 * lot with no booking behind it. Neither bookings page can sort or filter on
 * end date, so before this the answer meant paging through every active
 * booking by eye.
 *
 * The search cannot filter or sort on end date either, so the active bookings
 * are pulled — the same capped fetch {@link ./activeBookingsByLocationSkill}
 * uses — and the window is applied here, in the browser's day.
 */

import moment from 'moment';
import { locationBookingsPath } from './activeBookingsByLocationSkill';
import type {
  KnowledgeAnswer,
  KnowledgeBreakdownRow,
  KnowledgeContext,
  KnowledgeLink,
  KnowledgeQuery,
} from './knowledgeRegistry';
import { displayDate } from './lookupFormat';
import { classifyLookup, isHowToOrActionRequest } from './lookupRouting';
import { detectPeriod } from './periodParser';
import {
  fetchActiveBookings,
  MAX_ACTIVE_BOOKING_PAGES,
  type ActiveBookingRow,
  type ActiveBookingsAudience,
} from './requests/active-bookings-requests';
import { resolveUpcomingWindow, type UpcomingWindow } from './upcomingWindow';

const SLASH_DAY = 'MM/DD/YYYY';

/**
 * Month-to-month bookings are stored as running until 12/31/2200 (see
 * `BookingService.DATE_WE_WILL_ALL_BE_DEAD_BY`): they have no end date until
 * someone ends them. Anything this far out is one of those.
 */
const OPEN_ENDED_YEAR = 2100;

/** Most bookings listed one by one. Each row carries its own link. */
const MAX_ROWS = 15;

const BOOKINGS_PAGE: Record<ActiveBookingsAudience, string> = {
  supplier: '/bookings/supplier-bookings',
  buyer: '/bookings',
};

const BOOKING_WORD = /\b(?:bookings?|reservations?)\b/i;

/**
 * Present and future forms only. "Which bookings ended last month" is about
 * bookings that are no longer active, which this cannot see.
 */
const ENDING_WORD =
  /\b(?:end|ends|ending|expire|expires|expiring|expiration|expirations|finish|finishes|finishing|runs? out|running out)\b/i;

const MONTH_WORDS =
  'january|february|march|april|may|june|july|august|september|october|november|december|' +
  'jan|feb|apr|jun|jul|aug|sept|sep|oct|nov|dec';

/**
 * "The end of the month" names a time, not a booking ending — "how many active
 * bookings will I have at the end of the month" is a count. Removed before
 * looking for a word about ending.
 */
const END_OF_PERIOD = new RegExp(
  `\\bend\\s+of\\s+(?:the\\s+|this\\s+|next\\s+)?(?:day|today|week|month|quarter|year|${MONTH_WORDS})\\b` +
    '|\\b(?:month|quarter|year)[\\s-]end\\b',
  'gi',
);

/** Questions about bookings beginning or renewing, which have their own answers. */
const START_OR_RENEW =
  /\b(?:start|starts|starting|started|begin|begins|beginning|began|renew|renews|renewal|renewals|renewing|renewed)\b/i;

/**
 * "What happens after my booking ends" and "will I be charged if I stay past
 * the end" are about the consequences, not about which bookings end.
 */
const AFTERMATH = /\b(?:happens?|happened|what if|after|overstay\w*|overage\w*|late fees?|charged?|penalty)\b/i;

/** "Can I end my booking early", "is there a way to end my booking". */
const ASKS_TO_ACT = new RegExp(
  '\\b(?:(?:can|could|should|may)\\s+(?:i|we|you)|' +
    '(?:want|wants|need|needs|like|possible|able|way|trying|try|option)\\s+to)\\s+' +
    '(?:end|cancel|extend|stop|terminate)\\b',
  'i',
);

/** "When does it end", "how long is left": list every booking, not a window. */
const ASKS_WHEN = /\b(?:when|how long|how much longer|how many days)\b/i;

/** A booking number given without a label: "when does 1021445 end". */
const BARE_NUMBER = /\b(\d{5,})\b/;

/** Backward-looking periods from {@link detectPeriod}. */
const PAST_PERIODS = new Set([
  'yesterday',
  'lastWeek',
  'lastMonth',
  'lastQuarter',
  'lastYear',
  'lastNDays',
  'lastNWeeks',
  'lastNMonths',
  'lastNYears',
  'sinceDate',
]);

/**
 * Claims a question about which bookings are coming to an end.
 *
 * A claim rather than synonyms because the word that matters is a short one:
 * typo-tolerant matching reads "end" as "and", so "bookings end" would match
 * "bookings and invoices". It also keeps "show me bookings ending this week"
 * from being offered as a trip to the Bookings page. Defers to the record
 * lookups, so "when does booking 1021445 end" stays with the booking lookup.
 */
export const claimsExpiringBookings = (message: string): boolean => {
  if (!BOOKING_WORD.test(message)) return false;
  if (classifyLookup(message) !== null || isHowToOrActionRequest(message)) return false;
  if (START_OR_RENEW.test(message) || AFTERMATH.test(message) || ASKS_TO_ACT.test(message)) {
    return false;
  }
  return ENDING_WORD.test(message.replace(END_OF_PERIOD, ' '));
};

interface Ending {
  row: ActiveBookingRow;
  end: moment.Moment;
}

const pluralize = (count: number, singular: string, plural = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : plural}`;

const endOf = (row: ActiveBookingRow): moment.Moment =>
  moment(displayDate(row.endDate), SLASH_DAY, true);

const isOpenEnded = (end: moment.Moment): boolean => end.year() >= OPEN_ENDED_YEAR;

/** "today", "tomorrow", "in 12 days". */
const fromToday = (end: moment.Moment, today: moment.Moment): string => {
  const days = end.diff(today, 'days');
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
};

const bookingPath = (audience: ActiveBookingsAudience, orderNumber: string): string =>
  `${BOOKINGS_PAGE[audience]}?bookingNumber=${encodeURIComponent(orderNumber)}&status=ACTIVE`;

/** Who and where, for one booking: "for **Acme** at **Long Beach**". */
const partiesPhrase = (row: ActiveBookingRow, audience: ActiveBookingsAudience, showLocation: boolean): string => {
  const at = showLocation && row.locationName ? ` at **${row.locationName}**` : '';
  if (audience === 'supplier') {
    return `${row.buyerCompanyName ? ` for **${row.buyerCompanyName}**` : ''}${at}`;
  }
  return `${at}${row.supplierCompanyName ? ` (${row.supplierCompanyName})` : ''}`;
};

const spacesOf = (row: ActiveBookingRow): number =>
  typeof row.numberOfSpaces === 'number' && Number.isFinite(row.numberOfSpaces) && row.numberOfSpaces > 0
    ? row.numberOfSpaces
    : 0;

const breakdownRow = (
  { row, end }: Ending,
  audience: ActiveBookingsAudience,
  showLocation: boolean,
  today: moment.Moment,
): KnowledgeBreakdownRow => {
  const party = audience === 'supplier' ? row.buyerCompanyName : row.supplierCompanyName;
  const spaces = spacesOf(row);
  const label = [
    row.orderNumber ? `#${row.orderNumber}` : 'Booking',
    party,
    showLocation || audience === 'buyer' ? row.locationName : '',
    spaces ? pluralize(spaces, 'space') : '',
  ]
    .filter(Boolean)
    .join(' \u00b7 ');
  return {
    label,
    value: `${end.format(SLASH_DAY)} (${fromToday(end, today)})`,
    link: row.orderNumber
      ? { label: 'Open this booking', path: bookingPath(audience, row.orderNumber) }
      : undefined,
  };
};

/** "at **Long Beach**", "at your inactive locations", or '' for the whole account. */
const scopePhrase = (context: KnowledgeContext): string => {
  if (context.audience !== 'supplier' || context.wholeAccount) return '';
  if (context.scope.kind === 'STATUS') return ` at your ${context.scope.word} locations`;
  if (context.targets.length === 1) return ` at **${context.targets[0].name}**`;
  return ` at those ${context.targets.length} locations`;
};

const pageLink = (context: KnowledgeContext, audience: ActiveBookingsAudience): KnowledgeLink => {
  if (audience === 'supplier' && !context.wholeAccount && context.targets.length === 1) {
    return { label: 'View these bookings', path: locationBookingsPath(context.targets[0].id) };
  }
  return { label: 'Open active bookings', path: `${BOOKINGS_PAGE[audience]}?status=ACTIVE` };
};

const openEndedNote = (count: number, all: boolean): string => {
  if (count === 0) return '';
  if (all) {
    return count === 1
      ? ' It is month-to-month, so it has no end date and keeps renewing until it is ended.'
      : ` All ${count} are month-to-month, so they have no end date and keep renewing until they are ended.`;
  }
  return count === 1
    ? ' One more is month-to-month and keeps renewing until it is ended.'
    : ` ${count} more are month-to-month and keep renewing until they are ended.`;
};

const truncationNote = (checked: number, total: number): string =>
  checked < total
    ? ` I could only check the first ${checked} of your ${total} active bookings ` +
      `(${MAX_ACTIVE_BOOKING_PAGES} pages), so there may be more.`
    : '';

const BUYER_TIP =
  ' A booking cannot be extended \u2014 to keep a space longer, place a new booking before it ends.';

/** "When does 1021445 end": one booking, found among the active ones. */
const singleBookingAnswer = (
  number: string,
  rows: ActiveBookingRow[],
  audience: ActiveBookingsAudience,
  truncated: boolean,
  today: moment.Moment,
): KnowledgeAnswer => {
  const row = rows.find((candidate) => (candidate.orderNumber ?? '').trim() === number);
  if (!row) {
    const where = truncated ? 'among the active bookings I could check' : 'among your active bookings';
    return {
      text:
        `I could not find booking **#${number}** ${where}. If it has already ended or is not ` +
        `approved yet, ask me _"status of booking ${number}"_ to see where it stands.`,
    };
  }

  const end = endOf(row);
  const who = partiesPhrase(row, audience, true);
  const link: KnowledgeLink = { label: 'Open this booking', path: bookingPath(audience, number) };
  if (!end.isValid()) {
    return { text: `Booking **#${number}**${who} is active, but I could not read its end date.`, link };
  }
  if (isOpenEnded(end)) {
    return {
      text:
        `Booking **#${number}**${who} is month-to-month \u2014 it has no end date and keeps ` +
        'renewing until it is ended.',
      link,
    };
  }
  return {
    text:
      `Booking **#${number}**${who} ends on **${end.format(SLASH_DAY)}** (${fromToday(end, today)}).` +
      (audience === 'buyer' ? BUYER_TIP : ''),
    link,
  };
};

const pastAnswer = (label: string, audience: ActiveBookingsAudience): KnowledgeAnswer => ({
  text:
    `I can only see bookings that are still active, so I cannot list the ones that ended ${label}. ` +
    'Bookings that have ended are on the **Inactive** tab of Bookings.',
  link: { label: 'Open inactive bookings', path: `${BOOKINGS_PAGE[audience]}?status=INACTIVE` },
});

const windowLead = (
  ending: Ending[],
  inScope: number,
  window: UpcomingWindow,
  context: KnowledgeContext,
  audience: ActiveBookingsAudience,
  today: moment.Moment,
): string => {
  const scope = scopePhrase(context);
  const showLocation = context.wholeAccount || context.targets.length !== 1;
  const [first] = ending;
  const firstPhrase =
    `**#${first.row.orderNumber ?? '?'}**${partiesPhrase(first.row, audience, showLocation)}, ` +
    `on **${first.end.format(SLASH_DAY)}** (${fromToday(first.end, today)})`;

  const spaces = ending.reduce((sum, item) => sum + spacesOf(item.row), 0);
  const freeing = audience === 'supplier' && spaces > 0 ? `, freeing up ${pluralize(spaces, 'space')}` : '';

  if (inScope === 1) {
    return `Your only active booking${scope} ends ${window.label}${freeing}: ${firstPhrase}.`;
  }
  if (ending.length === 1) {
    return `One of your ${inScope} active bookings${scope} ends ${window.label}${freeing}: ${firstPhrase}.`;
  }
  const share =
    ending.length === inScope ? `All ${inScope} of your active bookings` : `${ending.length} of your ${inScope} active bookings`;
  return `**${share}**${scope} end ${window.label}${freeing}. The first is ${firstPhrase}.`;
};

const answer = async (context: KnowledgeContext): Promise<KnowledgeAnswer> => {
  const { message } = context;
  const audience: ActiveBookingsAudience = context.audience === 'buyer' ? 'buyer' : 'supplier';
  const window = resolveUpcomingWindow(message);

  const past = window.explicit ? null : detectPeriod(message);
  if (past && PAST_PERIODS.has(past.id)) return pastAnswer(past.label, audience);

  const sample = await fetchActiveBookings(audience);
  const truncated = sample.rows.length < sample.total;
  const today = moment().startOf('day');

  const number = message.match(BARE_NUMBER)?.[1];
  if (number) return singleBookingAnswer(number, sample.rows, audience, truncated, today);

  const targetIds = new Set(context.targets.map((target) => target.id));
  const scoped =
    audience === 'supplier' && !context.wholeAccount
      ? sample.rows.filter((row) => row.locationId !== undefined && targetIds.has(row.locationId))
      : sample.rows;

  const dated: Ending[] = scoped
    .map((row) => ({ row, end: endOf(row) }))
    .filter(({ end }) => end.isValid());
  const fixed = dated
    .filter(({ end }) => !isOpenEnded(end))
    .sort((a, b) => a.end.diff(b.end) || (a.row.orderNumber ?? '').localeCompare(b.row.orderNumber ?? ''));
  const openEnded = dated.length - fixed.length;

  const showLocation = context.wholeAccount || context.targets.length !== 1;
  const link = pageLink(context, audience);
  const note = truncationNote(sample.rows.length, sample.total);
  const scope = scopePhrase(context);

  if (scoped.length === 0) {
    // From a capped sample, none found is not none: the rest may be on pages not fetched.
    if (!truncated) return { text: `You have no active bookings${scope} right now.`, link };
    const text = scope
      ? `None of the first ${sample.rows.length} of your ${sample.total} active bookings are${scope}, ` +
        `but I could only check ${MAX_ACTIVE_BOOKING_PAGES} pages, so I cannot say whether there are any.`
      : `I could not load your ${sample.total} active bookings.`;
    return { text: `${text} Open the bookings page to check.`, link };
  }

  const listAll = !window.explicit && ASKS_WHEN.test(message);
  const start = moment(window.startDate);
  const last = moment(window.endDate);
  const ending = listAll
    ? fixed
    : fixed.filter(({ end }) => !end.isBefore(start, 'day') && !end.isAfter(last, 'day'));

  const rows = ending.slice(0, MAX_ROWS).map((item) => breakdownRow(item, audience, showLocation, today));
  const overflow = ending.length > MAX_ROWS ? ` The ${MAX_ROWS} soonest are listed below.` : '';
  const tip = audience === 'buyer' && ending.length > 0 ? BUYER_TIP : '';

  if (listAll) {
    if (ending.length === 0) {
      const text = openEnded > 0
        ? openEndedNote(openEnded, true).trim()
        : `I could not read the end dates of your active bookings${scope}.`;
      return { text: `${text}${note}`, link };
    }
    const [only] = ending;
    const lead =
      ending.length === 1
        ? `Your booking **#${only.row.orderNumber ?? '?'}**${partiesPhrase(only.row, audience, showLocation)} ` +
          `ends on **${only.end.format(SLASH_DAY)}** (${fromToday(only.end, today)}).`
        : `Here is when your ${pluralize(ending.length, 'active booking')}${scope} end, soonest first.`;
    return {
      text: `${lead}${openEndedNote(openEnded, false)}${overflow}${tip}${note}`,
      breakdown: ending.length === 1 ? undefined : rows,
      link,
    };
  }

  if (ending.length === 0) {
    const next = fixed.find(({ end }) => end.isAfter(last, 'day'));
    const nextSentence = next
      ? ` The next to end is **#${next.row.orderNumber ?? '?'}**${partiesPhrase(next.row, audience, showLocation)}, ` +
        `on **${next.end.format(SLASH_DAY)}** (${fromToday(next.end, today)}).`
      : '';
    const none =
      scoped.length === 1
        ? `Your only active booking${scope} does not end ${window.label}.`
        : `None of your ${scoped.length} active bookings${scope} end ${window.label}.`;
    return {
      text:
        `${none}${nextSentence}` +
        `${openEnded > 0 && !next ? openEndedNote(openEnded, fixed.length === 0) : ''}${note}`,
      link,
    };
  }

  return {
    text: `${windowLead(ending, scoped.length, window, context, audience, today)}${overflow}${tip}${note}`,
    breakdown: rows,
    link,
  };
};

export const expiringBookingsQuery: KnowledgeQuery = {
  id: 'expiringBookings',
  audiences: ['supplier', 'buyer'],
  synonyms: [
    'bookings ending soon',
    'bookings expiring soon',
    'bookings that end soon',
    'bookings about to end',
    'bookings about to expire',
    'bookings coming to an end',
    'expiring bookings',
    'booking expirations',
    'upcoming booking expirations',
    'upcoming booking end dates',
    'when do my bookings end',
    'when does my booking end',
    'when does my booking expire',
    'bookings ending this week',
    'bookings ending next week',
    'bookings ending this month',
    'bookings ending next month',
    'bookings expiring this month',
  ],
  claims: claimsExpiringBookings,
  example: 'which bookings end in the next 30 days',
  description:
    'Which active bookings end soon \u2014 in the next 30 days by default, or a window still to ' +
    'come such as this week, next month, in October or in the next 14 days \u2014 soonest first, ' +
    'with who and where, how many days are left and (for suppliers) how many spaces free up. ' +
    'Also answers "when does my booking end" by listing end dates. Month-to-month bookings have ' +
    'no end date and are noted separately. Looks ahead only; bookings that already ended are ' +
    'not covered.',
  answer,
};

export const __testing = { OPEN_ENDED_YEAR, MAX_ROWS, fromToday };
